import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError, login, me } from '../api.js';
import { safeNext } from '../lib/safeNext.js';
import { Button, Icon } from '../ui/index.js';

/**
 * Sign-in page. `?next=` carries the OAuth authorize URL when a connector
 * (Claude) sends an unauthenticated browser here, or `/brief/<uuid>` when a
 * Signal brief link was opened signed out; it is honoured only when
 * `safeNext` accepts it, and always with a full page load because
 * /oauth/authorize is a server-rendered page, not an SPA route.
 */
export default function Login(): JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'));

  // Already signed in (the session outlived the connector's redirect): go
  // straight on to the consent page. A 401 here just leaves the form showing;
  // the api wrapper never redirects while we are on /login.
  useEffect(() => {
    if (!next) return;
    let alive = true;
    me()
      .then(() => {
        if (alive) window.location.replace(next);
      })
      .catch(() => {
        /* not signed in: show the form */
      });
    return () => {
      alive = false;
    };
  }, [next]);

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError('');
    setBusy(true);
    let leaving = false;
    try {
      await login(email, password);
      if (next) {
        leaving = true; // full page load; keep the button disabled until it unloads
        window.location.assign(next);
      } else {
        navigate('/');
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError('Email or password is incorrect.');
      } else if (err instanceof ApiError && err.status === 503) {
        setError('Sign-in is unavailable right now.');
      } else {
        setError('Could not sign in. Check your connection and try again.');
      }
    } finally {
      if (!leaving) setBusy(false);
    }
  }

  return (
    <div className="login bc-login">
      <div className="login__card">
        <div className="login__brand">
          <span className="brand__mark bc-login__mark" aria-hidden="true">
            <Icon name="activity" size={30} />
          </span>
          <h1 className="login__title">Vitals</h1>
          <p className="login__hint">Health, work and life in one calm place.</p>
        </div>
        <form className="login__form" onSubmit={onSubmit}>
          {error && (
            <div className="login__error" role="alert">
              {error}
            </div>
          )}
          <div className="bc-login__fields">
            <input
              type="email"
              placeholder="Email"
              aria-label="Email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
            />
            <input
              type="password"
              placeholder="Password"
              aria-label="Password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <Button type="submit" size="lg" block disabled={busy}>
            {busy ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>
      </div>
    </div>
  );
}
